import { getReferralsApiUrl } from '../config/api';
import { sanitizeWorkflowPayload } from '../lib/smartFill/sanitizeWorkflowPayload';
const LOG_PREFIX = '[smart-fill]';
export class SmartFillError extends Error {
    constructor(message, debug) {
        super(message);
        Object.defineProperty(this, "debug", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = 'SmartFillError';
        this.debug = debug;
    }
}
function logSmartFill(step, data) {
    if (process.env.NODE_ENV === 'production')
        return;
    if (data !== undefined) {
        console.log(LOG_PREFIX, step, data);
    }
    else {
        console.log(LOG_PREFIX, step);
    }
}
function mapServerDebug(apiPath, serverDebug) {
    return {
        url: getReferralsApiUrl(apiPath),
        httpStatus: serverDebug.httpStatus,
        request: serverDebug.request,
        response: serverDebug.response,
    };
}
async function postReferralRaw(apiPath, body) {
    const url = getReferralsApiUrl(apiPath);
    const debug = {
        url,
        httpStatus: 0,
        request: body ?? {},
        response: null,
    };
    logSmartFill('request', { url, body });
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    debug.httpStatus = response.status;
    const rawText = await response.text();
    logSmartFill('response raw', rawText.slice(0, 500));
    let payload;
    try {
        payload = JSON.parse(rawText);
        debug.response = payload;
    }
    catch {
        debug.response = rawText;
        throw new SmartFillError(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`, debug);
    }
    if (payload.debug) {
        Object.assign(debug, mapServerDebug(apiPath, payload.debug));
    }
    if (!response.ok || payload.error) {
        throw new SmartFillError(payload.error ?? `Request failed (${response.status})`, debug);
    }
    return { payload, debug };
}
async function postReferralApi(apiPath, body) {
    const { payload, debug } = await postReferralRaw(apiPath, body);
    if (!payload.result) {
        throw new SmartFillError('API response did not include result', debug);
    }
    return withMappedDebug(apiPath, payload.result, debug);
}
function withMappedDebug(apiPath, result, fallback) {
    const serverResultDebug = result.debug;
    return {
        ...result,
        debug: serverResultDebug ? mapServerDebug(apiPath, serverResultDebug) : fallback,
    };
}
function delay(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
export async function runSmartFill(request) {
    return postReferralApi('/referrals/smart-fill', sanitizeWorkflowPayload(request));
}
const PARSE_PATH = '/referrals/document-parse';
const PARSE_POLL_INTERVAL_MS = 2000;
const PARSE_POLL_TIMEOUT_MS = 6 * 60 * 1000;
/** Tolerate the odd dropped poll; a parse in flight shouldn't die with it. */
const PARSE_POLL_MAX_ERRORS = 3;
/**
 * Start the parse, then poll until it finishes.
 *
 * Parses regularly outlast the 29s cap on a single workflow request, so the run
 * is started server-side and tracked by executionId from here.
 */
export async function runDocumentParse(request, onProgress) {
    const started = await postReferralRaw(PARSE_PATH, sanitizeWorkflowPayload(request));
    const executionId = started.payload.executionId?.trim();
    if (!executionId) {
        throw new SmartFillError('API response did not include executionId', started.debug);
    }
    const startedAtMs = Date.now();
    const deadline = startedAtMs + PARSE_POLL_TIMEOUT_MS;
    let consecutiveErrors = 0;
    while (Date.now() < deadline) {
        await delay(PARSE_POLL_INTERVAL_MS);
        let polled;
        try {
            polled = await postReferralRaw(`${PARSE_PATH}/status`, { executionId });
            consecutiveErrors = 0;
        }
        catch (err) {
            // A rejected request won't start working on the next try.
            if (err instanceof SmartFillError && err.debug.httpStatus === 400)
                throw err;
            consecutiveErrors += 1;
            if (consecutiveErrors >= PARSE_POLL_MAX_ERRORS)
                throw err;
            continue;
        }
        const status = polled.payload.status;
        if (!status) {
            throw new SmartFillError('Parse status response was empty', polled.debug);
        }
        onProgress?.({
            state: status.state,
            progress: status.progress,
            currentStep: status.currentStep,
            elapsedMs: Date.now() - startedAtMs,
        });
        if (status.state === 'failed') {
            throw new SmartFillError(status.error ?? 'Document parse failed', polled.debug);
        }
        if (status.state === 'completed') {
            if (!status.result) {
                throw new SmartFillError('Parse completed without a result', polled.debug);
            }
            return withMappedDebug(PARSE_PATH, status.result, polled.debug);
        }
    }
    throw new SmartFillError('Document parse is still running after 6 minutes. Try a smaller file.', started.debug);
}
